import Image from 'next/image';
import Link from 'next/link';

import { Box, Flex, FlexProps, Text } from '@chakra-ui/react';

import { Difficulty1Icon, Difficulty2Icon, Difficulty3Icon, StarIcon, StopWatchIcon } from '~/components/icons';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { getSchoolTheme } from '~/features/catalog/utils/get-school-theme';
import { useCourseLink } from '~/features/program/hooks/use-course-link';
import { getDifficultyLevel } from '~/features/program/utils/get-difficulty-data';
import { getRawDuration } from '~/features/program/utils/get-raw-duration';
import { useI18n } from '~/hooks/use-i18n';

/**
 * Narrower than `CatalogItem` so callers that trim their static props down to
 * what the cards render can still pass their payload straight through.
 */
export type CondensedCatalogCardItem = Pick<
  CatalogItem,
  'key' | 'slug' | 'title' | 'difficultyLevel' | 'rawDurationDisplay' | 'reviewSummary' | 'schools'
>;

export type CondensedCatalogCardProps = {
  catalogItem: CondensedCatalogCardItem;
  showDuration?: boolean;
  /**
   * Overrides the icon derived from the item's primary school. School landing
   * pages badge every card with the school being browsed, which is not always
   * the school the catalog taxonomy assigns the program to.
   */
  iconPath?: string | null;
  containerProps?: FlexProps;
};

export function CondensedCatalogCard({
  catalogItem,
  showDuration = true,
  iconPath,
  containerProps,
}: CondensedCatalogCardProps) {
  const { t } = useI18n();

  const resolvedIconPath = iconPath !== undefined ? iconPath : getSchoolTheme(catalogItem.schools[0]?.uri).iconPath;
  /**
   * An explicitly-passed badge is decorative — `schools[0].name` would
   * describe the taxonomy school, not the badge being displayed.
   */
  const iconAlt = iconPath !== undefined ? '' : catalogItem.schools[0]?.name ?? '';

  const localeDifficultyLevel = getDifficultyLevel(catalogItem.difficultyLevel, t);
  const localeRawDuration = catalogItem.rawDurationDisplay ? getRawDuration(catalogItem.rawDurationDisplay, t) : null;

  const courseLink = useCourseLink({ courseSlug: catalogItem.slug });

  return (
    <Flex
      as={Link}
      bg='white'
      border='solid 1px'
      borderColor='grey.100'
      borderRadius='8px'
      flexDirection='column'
      gap='12px'
      href={courseLink}
      p='16px'
      {...containerProps}
    >
      <Flex alignItems='center' flex={1} gap='16px'>
        {resolvedIconPath && (
          <Box
            flexShrink={0}
            height={{ base: '24px', md: '28px' }}
            position='relative'
            width={{ base: '24px', md: '28px' }}
          >
            <Image alt={iconAlt} quality={100} sizes='28px' src={resolvedIconPath} fill />
          </Box>
        )}
        <Text fontSize={{ base: '0.875rem', md: '1rem' }} fontWeight='600' noOfLines={1} wordBreak='break-all'>
          {catalogItem.title}
        </Text>
      </Flex>

      <Box bg='grey.100' h='1px' w='full' />

      <Flex alignItems='center' justifyContent='space-between'>
        <Flex alignItems='center' color='grey.500' gap={1}>
          {catalogItem.difficultyLevel && (
            <>
              {(catalogItem.difficultyLevel === 'Beginner' ||
                catalogItem.difficultyLevel === 'Discovery' ||
                catalogItem.difficultyLevel === 'Fluency') && (
                <Difficulty1Icon
                  height={{
                    base: '12px',
                    md: '16px',
                  }}
                  width={{
                    base: '12px',
                    md: '16px',
                  }}
                />
              )}
              {catalogItem.difficultyLevel === 'Intermediate' && (
                <Difficulty2Icon
                  height={{
                    base: '12px',
                    md: '16px',
                  }}
                  width={{
                    base: '12px',
                    md: '16px',
                  }}
                />
              )}
              {catalogItem.difficultyLevel === 'Advanced' && <Difficulty3Icon h='16px' w='16px' />}
              <Text fontSize='0.75rem' noOfLines={1} wordBreak='break-all'>
                {localeDifficultyLevel}
              </Text>
            </>
          )}
        </Flex>
        <Flex alignItems='center' color='grey.500' gap='32px'>
          <Flex alignItems='center' gap='6px'>
            {localeRawDuration && showDuration && (
              <>
                <StopWatchIcon h='16px' w='16px' />
                <Text fontSize='0.75rem' noOfLines={1} textTransform='lowercase' wordBreak='break-all'>
                  {localeRawDuration}
                </Text>
              </>
            )}
          </Flex>
          <Flex alignItems='center' gap='6px'>
            {!!catalogItem.reviewSummary.starsAverage && (
              <Flex alignItems='center' gap='4px'>
                <StarIcon color='orange.500' h='16px' w='16px' />
                <Text color='orange.700' fontSize='0.75rem' fontWeight='500'>
                  {catalogItem.reviewSummary.starsAverage}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
